/**
 * The price rules a studio's browser has to reach the same answer on.
 *
 * The floor and the ladder are decisions, not lookups: the submission form
 * snaps a typed amount to a rung as it is typed, and the checkout refuses
 * anything under the floor. Both sides have to agree without a round-trip,
 * which is why they live here rather than beside `resolvePrice`, which needs a
 * database and stays in the API.
 */

import { MINIMUM_TRANSACTED_PRICE_MINOR, type Currency } from './ledger';

export class PricingError extends Error {
  override readonly name = 'PricingError';
}

/**
 * Section 5.4. The fixed component of payment processing makes very cheap
 * titles unprofitable, so there is a minimum transacted price, applied after
 * discount rather than to list price.
 *
 * The specification states this floor in EUR. It is deliberately not converted
 * into other currencies here: section 18 is explicit that pricing is per-region
 * price tables and never FX conversion, so a floor in USD, GBP or PLN is a
 * commercial decision somebody has to make, not arithmetic. Until one is set,
 * selling in that currency fails loudly rather than silently selling below a
 * floor nobody chose.
 */
export function assertAboveFloor(currency: Currency, amountMinor: number): void {
  const floor = MINIMUM_TRANSACTED_PRICE_MINOR[currency];

  if (floor === undefined) {
    throw new PricingError(
      `No minimum transacted price is set for ${currency}. ` +
        'Section 5.4 defines the floor in EUR only, and it is a commercial decision ' +
        'per currency rather than an FX conversion. Set one before selling in this currency.',
    );
  }

  if (amountMinor < floor) {
    throw new PricingError(
      `A transacted price of ${amountMinor} is below the ${currency} floor of ${floor}. ` +
        'The floor applies after discount, not to list price.',
    );
  }
}

/**
 * The prices a title can be submitted at.
 *
 * **A submission picks from a ladder rather than typing a number.** The field
 * this replaces was `Price, in cents` over a bare `<input type="number">`, which
 * asked a studio to do two conversions in their head — currency to euros,
 * euros to minor units — before answering a question they had no way to get
 * wrong safely. What came back was 999 as often as it was 9.99, and 9 as often
 * as either.
 *
 * The rungs are the price points this market actually uses, so the answer is a
 * decision about where a game sits rather than an arithmetic exercise. Anything
 * bespoke is a conversation with us, which is what submitting a title starts.
 *
 * **The bottom rung is the floor**, deliberately and not by coincidence:
 * `MINIMUM_TRANSACTED_PRICE_MINOR` is 299 in EUR and section 5.4 makes that a
 * commercial decision rather than arithmetic, so a ladder that started lower
 * would be offering something the checkout would refuse. `pricing.test.ts`
 * holds the two together.
 *
 * **There is no free rung.** The same floor is why: payment processing has a
 * fixed cost per transaction and the store cannot sell at zero, which is the
 * sentence the Steam import already has to say when it reads a free game. A
 * free-to-play title on GOODBUILD is a commercial decision somebody has to take
 * before it is a form field.
 *
 * In EUR because the whole catalogue is. Section 18 is per-region price tables
 * and never FX conversion, so a second ladder in another currency is a set of
 * numbers somebody chooses, not this one multiplied.
 */
export const PRICE_POINTS_MINOR = [
  299, 499, 799, 999, 1499, 1999, 2499, 2999, 3999, 4999, 5999,
] as const;

/**
 * The rung closest to an amount from somewhere else.
 *
 * The Steam import is what this is for: Steam quotes 1799 and the ladder has no
 * such rung, so the form has to land on one rather than on nothing. Ties round
 * up, because the studio is about to see which rung it landed on and the
 * cheaper surprise is the one they are less likely to notice.
 *
 * Anything below the floor comes back as the floor. That is not a rounding
 * decision — nothing under 299 can be sold here at all, so there is no honest
 * nearer answer.
 */
export function nearestPricePoint(amountMinor: number): number {
  return PRICE_POINTS_MINOR.reduce((best, point) =>
    Math.abs(point - amountMinor) <= Math.abs(best - amountMinor) ? point : best,
  );
}
